import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import * as apiActions from 'api-actions'
import * as flashActions from 'redux-flash'
import { PostForm } from '../../../educator-portal/forms'
import { selectors as lhSelectors } from '../../global-reducer'
import { selectors as globalSelectors } from 'global-reducer'
import { LEARNING_HUB_DISCUSSION_TYPE_OPTIONS } from '../../constants'
import {
  getFormWideErrorMessage,
  serializeOptions,
  serializeAttachmentsForFormField,
} from 'utils'
import { startCase } from 'lodash'
import { HiddenSpan } from 'components'
import { Spinner } from 'lp-components'

const propTypes = {
  disciplines: PropTypes.arrayOf(Types.discipline).isRequired,
  fetchPostTopics: PropTypes.func.isRequired,
  fetchLearningHubCourses: PropTypes.func.isRequired,
  topics: PropTypes.arrayOf(Types.topicTag),
  learningHubCourses: PropTypes.arrayOf(PropTypes.object),
  learningHubCatalog: PropTypes.object,
  createOrUpdateDiscussion: PropTypes.func.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  postToEdit: Types.post,
  flashSuccessMessage: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  userMentionSuggestions: PropTypes.arrayOf(Types.mentionSuggestion),
  fetchMentionSuggestions: PropTypes.func.isRequired,
}

const defaultProps = {
  topics: null,
  postToEdit: null,
  learningHubCourses: [],
  learningHubCatalog: undefined,
}

function serializePostForEdit(post) {
  if (!post) {
    return {
      type: 'General',
      courses: [],
      attachments: [],
    }
  }

  const { disciplines, topics, attachments, courses, type } = post
  const disciplineIds = (disciplines || []).map(({ id }) => ({
    disciplineId: id,
  }))
  const topicIds = (topics || []).map(({ id }) => ({ topicId: id }))
  const courseRows = (courses || []).map(({ id }) => ({
    learningHubCourseId: id,
  }))
  return {
    ...post,
    type: type || 'General',
    disciplines: disciplineIds,
    topics: topicIds,
    courses: courseRows,
    attachments: serializeAttachmentsForFormField(attachments || []),
  }
}

function NewLearningHubPost({
  disciplines,
  fetchPostTopics,
  fetchLearningHubCourses,
  topics,
  learningHubCourses,
  learningHubCatalog,
  createOrUpdateDiscussion,
  onCancel,
  postToEdit,
  onSubmitSuccess,
  flashSuccessMessage,
  flashErrorMessage,
  userMentionSuggestions,
  fetchMentionSuggestions,
}) {
  const [formWideError, setFormWideError] = useState(null)
  const [submitFailCount, setSubmitFailCount] = useState(0)
  const containerRef = useRef()

  useEffect(() => {
    fetchPostTopics()
  }, [])

  useEffect(() => {
    fetchMentionSuggestions()
  }, [])

  useEffect(() => {
    if (learningHubCatalog === undefined) fetchLearningHubCourses()
  }, [learningHubCatalog, fetchLearningHubCourses])

  const schoolTypeOptions = Types.SCHOOL_TYPES.map((type) => ({
    key: startCase(type),
    value: type,
  }))

  const isEditing = !!postToEdit
  const hasLoaded =
    !!topics && !!userMentionSuggestions && learningHubCatalog !== undefined

  const courseFieldOptions = serializeOptions(learningHubCourses || [], {
    key: 'title',
    value: 'id',
  })

  return (
    <div ref={containerRef}>
      <h2>{isEditing ? 'Edit Discussion' : 'Start New Discussion'}</h2>
      <div role="alert" aria-atomic>
        {formWideError && (
          <p className="full-error-message">
            {formWideError} <HiddenSpan>{submitFailCount}</HiddenSpan>
          </p>
        )}
      </div>
      {hasLoaded ? (
        <PostForm
          initialValues={serializePostForEdit(postToEdit)}
          disciplineOptions={serializeOptions(disciplines)}
          schoolTypeOptions={schoolTypeOptions}
          topicOptions={serializeOptions(topics, { key: 'name' })}
          learningHubForum={true}
          discussionTypeOptions={LEARNING_HUB_DISCUSSION_TYPE_OPTIONS}
          courseOptions={courseFieldOptions}
          isEditing={isEditing}
          onCancel={onCancel}
          onSubmit={(post) => createOrUpdateDiscussion(post, post.id)}
          onSubmitSuccess={onSubmitSuccess}
          onSubmitFail={(errors, _, submitError) => {
            const msg = getFormWideErrorMessage(errors, submitError)
            setFormWideError(msg)
            setSubmitFailCount((sfc) => sfc + 1)
            containerRef.current.scrollIntoView({ behavior: 'smooth' })
          }}
          flashSuccessMessage={flashSuccessMessage}
          flashErrorMessage={flashErrorMessage}
          mentionSuggestions={userMentionSuggestions}
        />
      ) : (
        <Spinner />
      )}
    </div>
  )
}

NewLearningHubPost.propTypes = propTypes
NewLearningHubPost.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    disciplines: globalSelectors.definedDisciplines(state),
    topics: lhSelectors.postTopics(state),
    userMentionSuggestions: lhSelectors.userMentionSuggestions(state),
    learningHubCourses: lhSelectors.learningHubCourses(state),
    learningHubCatalog: lhSelectors.learningHubCatalog(state),
  }
}

const mapDispatchToProps = {
  fetchPostTopics: apiActions.fetchLearningHubPostTopics,
  fetchLearningHubCourses: apiActions.fetchLearningHubCourses,
  createOrUpdateDiscussion: apiActions.createOrUpdateLearningHubDiscussion,
  flashSuccessMessage: flashActions.flashSuccessMessage,
  flashErrorMessage: flashActions.flashErrorMessage,
  fetchMentionSuggestions: apiActions.fetchMentionSuggestions,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  NewLearningHubPost
)
